import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Card, CardHeader, CardContent, Box, Typography, IconButton, Avatar, ButtonBase, Chip, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import HandshakeIcon from '@mui/icons-material/Handshake';
import { useTranslation } from '../../../hooks/useTranslation';
import { listTickets, SERVICES } from '../../../Utils/ticketApi';

const STATUS_COLORS = {
    open: '#2196F3',
    in_progress: '#03A9F4',
    resolved: '#4CAF50',
    closed: '#9E9E9E',
};

const TICKET_LIMIT = 5;

const WidgetTicketsBase = ({ onRemove, isEditMode, service, icon, avatarColor, titleKey, subtitleKey }) => {
    const t = useTranslation('Dashboard.json');
    const tTickets = useTranslation('Tickets.json');
    const navigate = useNavigate();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(false);
        listTickets({ service, limit: TICKET_LIMIT })
            .then((result) => {
                if (!cancelled) setTickets(result.data || []);
            })
            .catch(() => {
                if (!cancelled) setError(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [service]);

    const handleRemove = (e) => {
        e.stopPropagation();
        e.preventDefault();
        onRemove();
    };

    const goToTickets = () => {
        navigate('/tickets', { state: { service } });
    };

    return (
        <Card
            elevation={0}
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '16px',
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                bgcolor: 'background.paper',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.08)',
                    borderColor: 'primary.light'
                }
            }}
        >
            <CardHeader
                title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ bgcolor: `${avatarColor}20`, width: 32, height: 32 }}>
                            {React.cloneElement(icon, { sx: { fontSize: 18, color: avatarColor } })}
                        </Avatar>
                        <Box>
                            <Typography sx={{ fontWeight: 600, fontSize: '15px', fontFamily: 'Poppins' }}>
                                {t(titleKey)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {t(subtitleKey)}
                            </Typography>
                        </Box>
                    </Box>
                }
                action={
                    isEditMode && (
                        <IconButton
                            className="widget-remove-btn"
                            onClick={handleRemove}
                            onMouseDown={(e) => e.stopPropagation()}
                            size="small"
                            sx={{
                                opacity: 0,
                                pointerEvents: 'none',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor: 'error.light',
                                    color: 'error.main'
                                }
                            }}
                        >
                            <CloseIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    )
                }
                sx={{
                    cursor: 'move',
                    pb: 1,
                    bgcolor: 'transparent',
                    '&:hover .widget-remove-btn': {
                        opacity: 1,
                        pointerEvents: 'all'
                    }
                }}
                className="widget-header"
            />
            <CardContent sx={{ flex: 1, pt: 0, overflow: 'auto' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', py: 3 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : error ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', py: 3 }}>
                        <Typography sx={{ fontSize: '12px', color: 'text.secondary', fontFamily: 'Poppins' }}>
                            {t('widget.tickets.error')}
                        </Typography>
                    </Box>
                ) : tickets.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', py: 3 }}>
                        <Typography sx={{ fontSize: '12px', color: 'text.secondary', fontFamily: 'Poppins' }}>
                            {t('widget.tickets.empty')}
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {tickets.map((ticket) => (
                            <ButtonBase
                                key={ticket.id}
                                onClick={goToTickets}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    p: 1.25,
                                    borderRadius: '10px',
                                    textAlign: 'left',
                                    border: '1px solid transparent',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        bgcolor: 'rgba(0,0,0,0.02)',
                                        borderColor: avatarColor,
                                        boxShadow: `0 2px 8px ${avatarColor}15`
                                    }
                                }}
                            >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        noWrap
                                        sx={{
                                            fontSize: 'clamp(12px, 1.2vw, 13px)',
                                            fontWeight: 600,
                                            fontFamily: 'Poppins',
                                            color: 'text.primary'
                                        }}
                                    >
                                        {ticket.title}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: 'clamp(10px, 1vw, 11px)',
                                            color: 'text.secondary',
                                            fontFamily: 'Poppins'
                                        }}
                                    >
                                        {new Date(ticket.updated_at || ticket.created_at).toLocaleDateString()}
                                    </Typography>
                                </Box>
                                <Chip
                                    label={tTickets(`tickets.status.${ticket.status}`)}
                                    size="small"
                                    sx={{
                                        fontSize: 'clamp(9px, 1vw, 10px)',
                                        fontFamily: 'Poppins',
                                        height: 20,
                                        bgcolor: `${STATUS_COLORS[ticket.status] || '#9E9E9E'}20`,
                                        color: STATUS_COLORS[ticket.status] || '#9E9E9E',
                                        fontWeight: 600
                                    }}
                                />
                            </ButtonBase>
                        ))}
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export const WidgetTicketsSupport = ({ onRemove, isEditMode }) => (
    <WidgetTicketsBase
        onRemove={onRemove}
        isEditMode={isEditMode}
        service={SERVICES.SUPPORT}
        icon={<SupportAgentIcon />}
        avatarColor="#FF7233"
        titleKey="widget.tickets_support.title"
        subtitleKey="widget.tickets_support.subtitle"
    />
);

export const WidgetTicketsCommercial = ({ onRemove, isEditMode }) => (
    <WidgetTicketsBase
        onRemove={onRemove}
        isEditMode={isEditMode}
        service={SERVICES.COMMERCIAL}
        icon={<HandshakeIcon />}
        avatarColor="#2196F3"
        titleKey="widget.tickets_commercial.title"
        subtitleKey="widget.tickets_commercial.subtitle"
    />
);
